/**
 * Track metadata + display helpers.
 *
 * The platform supports two student populations:
 *  - High-school Bagrut tiers: 3u, 4u, 5u (3/4/5 יח״ל)
 *  - Middle-school grades: ms7=ז׳, ms8=ח׳, ms9=ט׳
 *
 * Track codes stay short ASCII so they round-trip cleanly through JWTs,
 * URLs, and the backend curriculum.tracks filter. Display strings live
 * here, gated by language.
 */

export type Track =
  | '3u' | '4u' | '5u'
  | 'ms7' | 'ms8' | 'ms9'
  | 'el1' | 'el2' | 'el3' | 'el4' | 'el5' | 'el6';

export type TrackKind = 'elementary' | 'middle_school' | 'bagrut';

export interface TrackInfo {
  id: Track;
  kind: TrackKind;
  /** Hebrew display, suitable for top-nav / cards. */
  displayHe: string;
  /** English fallback. */
  displayEn: string;
  /** Short description for the track picker. */
  descHe: string;
  /** Theme color anchor. */
  accent: string;
  /** Emoji symbol. */
  icon: string;
}

export const TRACKS: TrackInfo[] = [
  // Elementary — youngest first, warm/playful palette
  { id: 'el1', kind: 'elementary', displayHe: 'כיתה א׳', displayEn: 'Grade 1', descHe: 'מנייה, חיבור וחיסור עד 20', accent: '#fbbf24', icon: '🧸' },
  { id: 'el2', kind: 'elementary', displayHe: 'כיתה ב׳', displayEn: 'Grade 2', descHe: 'ערך מקום, חישוב עד 100', accent: '#f97316', icon: '🐣' },
  { id: 'el3', kind: 'elementary', displayHe: 'כיתה ג׳', displayEn: 'Grade 3', descHe: 'כפל, חילוק, שברים בסיסיים', accent: '#f59e0b', icon: '🦊' },
  { id: 'el4', kind: 'elementary', displayHe: 'כיתה ד׳', displayEn: 'Grade 4', descHe: 'מספרים רב-ספרתיים, שברים מתקדמים', accent: '#84cc16', icon: '🐬' },
  { id: 'el5', kind: 'elementary', displayHe: 'כיתה ה׳', displayEn: 'Grade 5', descHe: 'שברים עשרוניים, אחוזים, יחס', accent: '#10b981', icon: '🦉' },
  { id: 'el6', kind: 'elementary', displayHe: 'כיתה ו׳', displayEn: 'Grade 6', descHe: 'הכנה לחטיבה — אלגברה מוקדמת', accent: '#06b6d4', icon: '🦅' },
  // Middle school
  { id: 'ms7', kind: 'middle_school', displayHe: 'כיתה ז׳', displayEn: 'Grade 7', descHe: 'מעבר משפה יומיומית לשפה מתמטית', accent: '#22d3ee', icon: '🌱' },
  { id: 'ms8', kind: 'middle_school', displayHe: 'כיתה ח׳', displayEn: 'Grade 8', descHe: 'קשרים, ייצוגים ובחירת דרך', accent: '#8b5cf6', icon: '🌿' },
  { id: 'ms9', kind: 'middle_school', displayHe: 'כיתה ט׳', displayEn: 'Grade 9', descHe: 'הכנה לחשיבה של תיכון', accent: '#f472b6', icon: '🌳' },
  // High-school Bagrut tiers
  { id: '3u', kind: 'bagrut', displayHe: '3 יח״ל', displayEn: '3 units', descHe: 'בסיס לבגרות', accent: '#22c55e', icon: '🟢' },
  { id: '4u', kind: 'bagrut', displayHe: '4 יח״ל', displayEn: '4 units', descHe: 'מסלול הרחבה', accent: '#eab308', icon: '🟡' },
  { id: '5u', kind: 'bagrut', displayHe: '5 יח״ל', displayEn: '5 units', descHe: 'מסלול מתקדם', accent: '#ef4444', icon: '🔴' },
];

const BY_ID: Record<string, TrackInfo> = Object.fromEntries(TRACKS.map(t => [t.id, t]));

export function getTrackInfo(track: string | undefined | null): TrackInfo | null {
  if (!track) return null;
  return BY_ID[track] ?? null;
}

/** Short display string. Defaults to Hebrew. */
export function trackDisplay(track: string | undefined | null, lang: string = 'he'): string {
  const info = getTrackInfo(track);
  if (!info) return track || '';
  return lang === 'en' ? info.displayEn : info.displayHe;
}

export function isElementary(track: string | undefined | null): boolean {
  return getTrackInfo(track)?.kind === 'elementary';
}

export function isMiddleSchool(track: string | undefined | null): boolean {
  return getTrackInfo(track)?.kind === 'middle_school';
}

export function isBagrut(track: string | undefined | null): boolean {
  return getTrackInfo(track)?.kind === 'bagrut';
}
